<script setup>
import { ref, watch, computed } from 'vue'
import { router } from '@inertiajs/vue3'
import UiModal from '@/Components/UI/UiModal.vue'
import UiInput from '@/Components/UI/UiInput.vue'
import UiSelect from '@/Components/UI/UiSelect.vue'
import UiButton from '@/Components/UI/UiButton.vue'
import { useI18n } from 'vue-i18n'

const props = defineProps({
  open:      { type: Boolean, default: false },
  subRental: { type: Object,  default: null }
})
const emit = defineEmits(['update:open'])
const { t } = useI18n()

const amount    = ref('')
const paid_on   = ref(new Date().toISOString().slice(0, 10))
const method    = ref('cash')
const reference = ref('')
const notes     = ref('')
const submitting = ref(false)

watch(() => props.open, (val) => {
  if (val) {
    amount.value = ''
    paid_on.value = new Date().toISOString().slice(0, 10)
    method.value = 'cash'
    reference.value = ''
    notes.value = ''
  }
})

const methodOptions = computed(() => [
  { value: 'cash',          label: t('supplier_payments.methods.cash') },
  { value: 'card',          label: t('supplier_payments.methods.card') },
  { value: 'bank_transfer', label: t('supplier_payments.methods.bank_transfer') },
  { value: 'online',        label: t('supplier_payments.methods.online') }
])

function close() {
  emit('update:open', false)
}

function submit() {
  if (!props.subRental) return
  const cents = Math.round(parseFloat(amount.value) * 100)
  if (!cents || cents <= 0) return
  submitting.value = true
  router.post(`/sub_rentals/${props.subRental.id}/supplier_payments`, {
    supplier_payment: {
      amount_cents: cents,
      paid_on:      paid_on.value,
      method:       method.value,
      reference:    reference.value,
      notes:        notes.value
    }
  }, {
    preserveScroll: true,
    onFinish: () => {
      submitting.value = false
      close()
    }
  })
}
</script>

<template>
  <UiModal :open="open" :title="t('supplier_payments.recordTitle')" @close="close">
    <form class="space-y-3" @submit.prevent="submit">
      <div>
        <label class="block text-sm mb-1">{{ t('supplier_payments.amount') }}</label>
        <UiInput v-model="amount" type="number" min="0.01" step="0.01" required />
      </div>
      <div>
        <label class="block text-sm mb-1">{{ t('supplier_payments.paid_on') }}</label>
        <UiInput v-model="paid_on" type="date" required />
      </div>
      <div>
        <label class="block text-sm mb-1">{{ t('supplier_payments.method') }}</label>
        <UiSelect v-model="method" :options="methodOptions" />
      </div>
      <div>
        <label class="block text-sm mb-1">{{ t('supplier_payments.reference') }}</label>
        <UiInput v-model="reference" />
      </div>
      <div>
        <label class="block text-sm mb-1">{{ t('supplier_payments.notes') }}</label>
        <UiInput v-model="notes" />
      </div>
      <div class="flex justify-end gap-2 pt-2">
        <UiButton type="button" variant="ghost" @click="close">
          {{ t('common.cancel') }}
        </UiButton>
        <UiButton type="submit" variant="primary" :loading="submitting">
          {{ t('supplier_payments.save') }}
        </UiButton>
      </div>
    </form>
  </UiModal>
</template>
